import { useMemo, useState } from 'react'
import { Package } from 'lucide-react'
import type {
  ClienteAgradaDetail,
  ClienteAgradaProposalSnapshot,
  ClienteAgradaServicoContratado,
} from '@/services/clientesService'
import { formatFinanceStatus } from '@/hooks/useFinanceiroPage'
import { FinanceDetailSectionCard } from '@/components/Financeiro/FinanceDetailSectionCard'
import { closedScopeTipoLabel } from '@/components/Financeiro/financeClosedScope'
import {
  ClosedScopeItemDetailModal,
  FinanceClosedScopeLineRow,
} from '@/components/Financeiro/ClosedScopeItemDetailModal'
import { buildClosedScopePlanViews } from '@/components/Financeiro/financeClosedScope'
import { canViewFinanceFinancialValues } from '@/components/Financeiro/financeGestorVisibility'
import type { FinanceProposalSnapshotRow } from '@/services/financeService'
import { CrmDataTable, crmTableHeadClass, crmTableRowClass } from '@/components/ui/CrmDataTable'
import { formatCurrency } from '@/utils/format-currency'
import { formatDate } from '@/utils/format-date'
import type { PublicPlanView } from '@/utils/propostaPublicScope'
import { useAuthStore } from '@/stores/authStore'
import { displayOrDash } from './clienteAgradaDetailUtils'

type Props = {
  detail: ClienteAgradaDetail
}

function formatPeriodo(
  inicio: string | null,
  fim: string | null,
  renovacao: string | null,
): string {
  const parts: string[] = []
  if (inicio) parts.push(`Início ${formatDate(inicio)}`)
  if (fim) parts.push(`Fim ${formatDate(fim)}`)
  else if (renovacao) parts.push(`Renovação ${formatDate(renovacao)}`)
  else if (inicio) parts.push('Em vigência')
  return parts.length > 0 ? parts.join(' · ') : '—'
}

function formatValor(mensal: number | null, total: number | null): string {
  if (mensal != null) return `${formatCurrency(mensal)}/mês`
  if (total != null) return formatCurrency(total)
  return '—'
}

function toFinanceSnapshot(snapshot: ClienteAgradaProposalSnapshot): FinanceProposalSnapshotRow {
  return {
    id: snapshot.id,
    versao: snapshot.versao,
    totalFinal: snapshot.totalFinal,
    pdfUrl: snapshot.pdfUrl,
    createdAt: snapshot.createdAt,
    payloadJson:
      snapshot.payloadJson && typeof snapshot.payloadJson === 'object' && !Array.isArray(snapshot.payloadJson)
        ? (snapshot.payloadJson as FinanceProposalSnapshotRow['payloadJson'])
        : { items: Array.isArray(snapshot.payloadJson) ? snapshot.payloadJson : [] },
  }
}

function ServicoEscopoItens({
  servico,
  canViewFinancialValues,
  onOpenPlan,
}: {
  servico: ClienteAgradaServicoContratado
  canViewFinancialValues: boolean
  onOpenPlan: (plan: PublicPlanView) => void
}) {
  const planById = useMemo(() => {
    if (!servico.proposalSnapshot) return new Map<string, PublicPlanView>()
    const views = buildClosedScopePlanViews(toFinanceSnapshot(servico.proposalSnapshot))
    return new Map(views.map((plan) => [plan.item.id, plan]))
  }, [servico.proposalSnapshot])

  return (
    <div className='rounded-xl border border-white/[0.06] bg-white/[0.02] p-4'>
      <p className='mb-1 text-sm font-semibold text-text-primary'>
        {servico.projetoNome ?? servico.dealNome ?? 'Contrato'}
      </p>
      {servico.servicosText && (
        <p className='mb-3 text-xs leading-relaxed text-text-muted'>{servico.servicosText}</p>
      )}
      <ul className='space-y-2'>
        {(servico.itens ?? []).map((item) => {
          const plan = planById.get(item.id)
          if (plan) {
            return (
              <FinanceClosedScopeLineRow
                key={item.id}
                descricao={item.descricao}
                tipo={item.tipo as 'plano' | 'servico' | 'avulso'}
                quantidade={item.quantidade}
                total={item.total ?? undefined}
                scopeCount={plan.scopeCount}
                canViewFinancialValues={canViewFinancialValues}
                onOpen={() => onOpenPlan(plan)}
              />
            )
          }

          return (
            <li
              key={item.id}
              className='flex flex-wrap items-center justify-between gap-2 rounded-xl border border-white/[0.06] bg-white/[0.03] p-3'
            >
              <div className='min-w-0'>
                <p className='font-medium text-text-primary'>{item.descricao}</p>
                <p className='text-xs text-text-muted'>
                  {closedScopeTipoLabel(item.tipo as 'plano' | 'servico' | 'avulso')} · Qtd{' '}
                  {item.quantidade}
                </p>
              </div>
              {canViewFinancialValues && item.total != null && (
                <span className='shrink-0 text-sm font-medium tabular-nums text-text-primary'>
                  {formatCurrency(item.total)}
                </span>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

export function ClienteAgradaServicosTab({ detail }: Props) {
  const userRole = useAuthStore((s) => s.user?.role)
  const canViewFinancialValues = canViewFinanceFinancialValues(userRole)
  const [selectedPlan, setSelectedPlan] = useState<PublicPlanView | null>(null)
  const servicos = detail.servicos ?? []
  const headClass = crmTableHeadClass()

  if (servicos.length === 0) {
    return (
      <FinanceDetailSectionCard title='Serviços contratados' icon={Package} accent='#6366f1'>
        <div className='py-10 text-center'>
          <p className='font-medium text-text-primary'>Nenhum serviço contratado registrado</p>
          <p className='mt-1 text-sm text-text-muted'>
            Os serviços aparecem após fechamento do deal, projeto financeiro ou contrato C4.
          </p>
        </div>
      </FinanceDetailSectionCard>
    )
  }

  return (
    <div className='space-y-4'>
      <FinanceDetailSectionCard
        title='Serviços contratados'
        icon={Package}
        accent='#6366f1'
        headerExtra={
          <span className='text-xs text-text-muted'>
            {servicos.length} contrato{servicos.length === 1 ? '' : 's'}
          </span>
        }
      >
        <CrmDataTable>
          <table className='w-full text-sm'>
            <thead>
              <tr className={headClass}>
                <th className='px-4 py-3 text-left'>Serviços / escopo</th>
                <th className='px-4 py-3 text-left'>Projeto / deal</th>
                <th className='px-4 py-3 text-left'>Vigência</th>
                <th className='px-4 py-3 text-left'>Status</th>
                <th className='px-4 py-3 text-right'>Valor</th>
              </tr>
            </thead>
            <tbody>
              {servicos.map((s) => (
                <tr key={s.id} className={crmTableRowClass(false)}>
                  <td className='px-4 py-3 align-top'>
                    <p className='font-medium text-text-primary'>
                      {displayOrDash(s.servicosText ?? (s.itens ?? [])[0]?.descricao)}
                    </p>
                    {s.plataforma && (
                      <p className='mt-0.5 text-xs text-text-muted'>Plataforma: {s.plataforma}</p>
                    )}
                    {s.recorrencia && (
                      <p className='mt-0.5 text-xs text-text-muted'>
                        Recorrência: {s.recorrencia}
                      </p>
                    )}
                  </td>
                  <td className='px-4 py-3 align-top'>
                    <p className='font-medium text-text-primary'>
                      {s.projetoNome ?? s.dealNome ?? '—'}
                    </p>
                    {s.dealNome && s.projetoNome && s.dealNome !== s.projetoNome && (
                      <p className='mt-0.5 text-xs text-text-muted'>Deal: {s.dealNome}</p>
                    )}
                  </td>
                  <td className='px-4 py-3 align-top text-text-muted'>
                    {formatPeriodo(s.dataInicio, s.dataFim, s.dataRenovacao)}
                  </td>
                  <td className='px-4 py-3 align-top'>
                    <span className='rounded-full bg-white/[0.06] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-text-muted'>
                      {s.projetoStatus ? formatFinanceStatus(s.projetoStatus) : '—'}
                    </span>
                  </td>
                  <td className='px-4 py-3 align-top text-right font-medium tabular-nums text-text-primary'>
                    {formatValor(s.valorMensal, s.valorTotal)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CrmDataTable>
      </FinanceDetailSectionCard>

      {servicos.some((s) => (s.itens ?? []).length > 0) && (
        <FinanceDetailSectionCard title='Escopo do serviço' icon={Package} accent='#987de8'>
          <p className='mb-4 text-sm text-text-muted'>
            Clique em um item para ver entregáveis, categorias e detalhes do plano contratado.
          </p>
          <div className='space-y-4'>
            {servicos
              .filter((s) => (s.itens ?? []).length > 0)
              .map((s) => (
                <ServicoEscopoItens
                  key={`itens-${s.id}`}
                  servico={s}
                  canViewFinancialValues={canViewFinancialValues}
                  onOpenPlan={setSelectedPlan}
                />
              ))}
          </div>
        </FinanceDetailSectionCard>
      )}

      {selectedPlan && (
        <ClosedScopeItemDetailModal
          plan={selectedPlan}
          canViewFinancialValues={canViewFinancialValues}
          onClose={() => setSelectedPlan(null)}
        />
      )}
    </div>
  )
}
